/**
 * THE PRICE LADDER, AS ROWS (GSD revamp, 2026-10-15).
 *
 * Every figure here is derived from src/config/workUnits.ts, the single source of prices (its backend twin and
 * Stripe's TIER_CONFIG mirror it). Nothing is typed twice: `/`, `/pricing`, `/subscription`, the ROI calculator,
 * llms.txt and the JSON-LD offers all read these helpers, so the same inputs give the same numbers everywhere
 * (acceptance criterion 2). Words live in each page's content module, never here.
 */
import {
  MONTHLY_WU_PER_PRESTON,
  STANDARD_RATE_USD_PER_WU,
  TYPICAL_MONTH,
  WORK_RATE_CARD,
  WU_PRICING,
  annualSeatRung,
  formatPriceUsd,
  hireQuote,
  singleProductCeiling,
  type AnnualRung,
  type HireCadence,
} from './workUnits';
import type { LadderTier } from './planTiers';

export type LadderRungKey = 'standard' | 'monthly' | AnnualRung['key'];

export interface LadderRow {
  key: LadderRungKey;
  /** Per Prest0n employee per month. */
  monthlyUsd: number;
  /** $ per PRO Work Unit (pr0), as published (rounded to the cent). */
  usdPerWu: number;
  /** % saved against the standard rate, as published; null on the standard rate. */
  savePct: number | null;
  /** The team sizes the rung sells, inclusive (the standard rate and monthly take any size the ladder sells). */
  minSeats: number;
  maxSeats: number;
  /** The tier a hire on this rung buys; null on the standard rate, which is a rate, not a plan. */
  tier: LadderTier | null;
}

/** The published ladder, standard rate first, then monthly, then every annual rung, in the order the pricing page lists it. */
export const PRICE_LADDER: readonly LadderRow[] = [
  { key: 'standard', monthlyUsd: WU_PRICING.standard.monthlyUsd, usdPerWu: WU_PRICING.standard.usdPerWu, savePct: null, minSeats: 1, maxSeats: WU_PRICING.selfServeMaxSeats, tier: null },
  { key: 'monthly', monthlyUsd: WU_PRICING.monthly.monthlyUsd, usdPerWu: WU_PRICING.monthly.usdPerWu, savePct: WU_PRICING.monthly.savePct, minSeats: 1, maxSeats: WU_PRICING.selfServeMaxSeats, tier: WU_PRICING.monthly.tier },
  ...WU_PRICING.annualRungs.map((r): LadderRow => ({
    key: r.key,
    monthlyUsd: r.monthlyUsd,
    usdPerWu: r.usdPerWu,
    savePct: r.savePct,
    minSeats: r.minSeats,
    maxSeats: r.maxSeats,
    tier: r.tier,
  })),
];

/** The annual rungs alone, smallest team first. */
export const ANNUAL_LADDER: readonly LadderRow[] = PRICE_LADDER.slice(2);

/** The lowest published $ per pr0 ("from $1.04 per work unit"): the largest annual rung. */
export const LOWEST_USD_PER_WU: number = Math.min(...PRICE_LADDER.map((r) => r.usdPerWu));

/** From this many Prest0n employees, larger organizations book a meeting (no price past the last rung is published). */
export const BY_MEETING_FROM: number = WU_PRICING.selfServeMaxSeats + 1;

/**
 * The primary offer (rulings R2, R9): three Prest0n employees on an annual commitment billed monthly, the count every
 * picker starts at and every headline prices. It carries the old one-Prest0n discount for a lower commitment. The price
 * headline's words (./priceHeadlineWords.ts, which Prest0n's facts share byte for byte) carry the same count for the
 * server, held equal to this one by tests/landing/priceHeadline.test.tsx.
 */
export const PRIMARY_OFFER_SEATS = 3;

/** The ladder row for a cadence and team size (prepay is not a rung: it is a discount on the annual rung). */
export function ladderRowFor(cadence: Exclude<HireCadence, 'prepay'> | 'standard', seats = 1): LadderRow {
  if (cadence === 'standard') return PRICE_LADDER[0];
  if (cadence === 'monthly') return PRICE_LADDER[1];
  const rung = annualSeatRung(seats);
  return ANNUAL_LADDER.find((r) => r.key === rung.key) ?? ANNUAL_LADDER[ANNUAL_LADDER.length - 1];
}

export interface PrepayRow {
  seats: number;
  /** The annual rung prepay discounts. */
  rung: LadderRow;
  discountPct: number;
  /** Monthly equivalent per Prest0n employee after the prepay discount, cents kept ($558.40). */
  perSeatMonthlyUsd: number;
  /** The one payment for the year, for the whole team, cents kept ($6,700.80 for one). */
  upfrontTotalUsd: number;
}

/** Prepay the year: an extra WU_PRICING.prepayDiscountPct % off the team size's annual rate, paid once. Never bonus units. */
export function prepayRow(seats = 1): PrepayRow {
  const q = hireQuote('prepay', seats);
  return {
    seats: q.seats,
    rung: ladderRowFor('annual', q.seats),
    discountPct: WU_PRICING.prepayDiscountPct,
    perSeatMonthlyUsd: q.perSeatMonthlyUsd,
    upfrontTotalUsd: q.upfrontTotalUsd as number,
  };
}

/** Round to the cent, the way a price is shown. */
export const toCents = (usd: number): number => Math.round(usd * 100) / 100;

export interface RateCardRow {
  task: string;
  wu: number;
  unit: string;
  receive: string;
  /** Dollars for one of it at the chosen $/pr0 (memo: 8 × $1.45 = $11.60 on the three-employee annual rung). */
  usd: number;
}

/** The rate card priced at a ladder rung's $ per pr0. */
export function rateCardAt(usdPerWu: number): RateCardRow[] {
  return WORK_RATE_CARD.map((r) => ({ task: r.task, wu: r.wu, unit: r.unit, receive: r.receive, usd: toCents(r.wu * usdPerWu) }));
}

export interface CapacityRow {
  key: string;
  item: string;
  count: number;
  wuEach: number;
  /** All of one kind: the whole month's allocation spent on this item alone. */
  allOfOneKind: number;
}

/** What one Prest0n employee's monthly allocation buys: the typical month, and each item's ceiling alone. */
export const CAPACITY: readonly CapacityRow[] = TYPICAL_MONTH.map((r) => ({
  key: r.key,
  item: r.item,
  count: r.count,
  wuEach: r.wuEach,
  allOfOneKind: singleProductCeiling(r.wuEach),
}));

export { MONTHLY_WU_PER_PRESTON, STANDARD_RATE_USD_PER_WU, formatPriceUsd };
